import React, { useState, useEffect } from "react";
import dayjs from "dayjs";
import { useSnackbar } from "notistack";
import { useHistory } from "react-router-dom";
import { Box, Typography, Grid, Paper, Divider, Chip, LinearProgress, TextField, Dialog, Button, DialogTitle, DialogContent } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";

import { useFetchMostRecentWorkshopLine, useCreateWorkshopLineMutation, useStopWorkshopLineMutation, useDeactivateWorkshopOrderMutation, useUpdateWorkshopLineMutation } from "../../api";
import LoadingButton from "../../components/LoadingButton/LoadingButton";

function WorkshopLineClock({ equipmentId, workshopOrderId, userId }) {
    const [sessionState, setSessionState] = useState("inactive"); // inactive, ongoing, and mostRecent
    const [employeeComment, setEmployeeComment] = useState(null);
    const [isEmployeeCommentDialogOpen, setIsEmployeeCommentDialogOpen] = useState(false);

    const [duration, setDuration] = useState(null);

    const { enqueueSnackbar } = useSnackbar();
    const history = useHistory();

    const { data: workshopLine, isLoading: isLoadingWorkshopLine, refetch: refetchWorkshopLine } = useFetchMostRecentWorkshopLine(workshopOrderId, userId, { retry: 1 });

    const { id: workshopLineId = null, checkIn = null, checkOut = null, comment = null } = workshopLine || {};

    useEffect(() => {
        let interval = null;

        if (workshopLine) {
            if (workshopLine.checkIn && workshopLine.checkOut) {
                setSessionState("mostRecent");

                // Calculate duration based on checkIn and checkOut times
                const start = new Date(workshopLine.checkIn);
                const end = new Date(workshopLine.checkOut);
                setDuration((end.getTime() - start.getTime()) / 1000 / 60 / 60); // in hours
            } else if (workshopLine.checkIn && !workshopLine.checkOut) {
                setSessionState("ongoing");

                // Calculate ongoing duration based on checkIn time
                interval = setInterval(() => {
                    const now = new Date();
                    const start = new Date(workshopLine.checkIn);
                    setDuration((now.getTime() - start.getTime()) / 1000 / 60 / 60); // in hours
                }, 1000); // Update every second
            } else {
                setSessionState("inactive");
                setDuration(null);
            }
            if (workshopLine.comment) {
                setEmployeeComment(workshopLine.comment);
            }
        }
        // Cleanup interval on unmount or when workshopLine data changes
        return () => {
            if (interval) clearInterval(interval);
        };
    }, [workshopLine]);

    const { isLoading: isLoadingCreateWorkline, mutate: createWorkline } = useCreateWorkshopLineMutation({
        onSuccess: () => {
            enqueueSnackbar("Work session started successfully", {
                variant: "success",
                anchorOrigin: { vertical: "bottom", horizontal: "center" },
                autoHideDuration: 3000,
            });
            setSessionState("ongoing");
            setEmployeeComment("");
            refetchWorkshopLine();
        },
        onError: () => {
            enqueueSnackbar("Error occurred while starting work session", {
                variant: "error",
                anchorOrigin: { vertical: "bottom", horizontal: "center" },
                autoHideDuration: 3000,
            });
        },
    });

    const { isLoading: isLoadingStopWorkline, mutate: stopWorkline } = useStopWorkshopLineMutation({
        onSuccess: () => {
            enqueueSnackbar("Work session ended successfully", {
                variant: "success",
                anchorOrigin: { vertical: "bottom", horizontal: "center" },
                autoHideDuration: 3000,
            });
            setSessionState("mostRecent");
            setEmployeeComment("");
            refetchWorkshopLine();
        },
        onError: () => {
            enqueueSnackbar("Error occurred while ending work session", {
                variant: "error",
                anchorOrigin: { vertical: "bottom", horizontal: "center" },
                autoHideDuration: 3000,
            });
        },
    });

    const { isLoading: isLoadingUpdateWorkshopLine, mutate: updateWorkshopLine } = useUpdateWorkshopLineMutation({
        onSuccess: () => {
            enqueueSnackbar("Comment added successfully", {
                variant: "success",
                anchorOrigin: { vertical: "bottom", horizontal: "center" },
                autoHideDuration: 3000,
            });
            refetchWorkshopLine();
        },
        onError: () => {
            enqueueSnackbar("Error occurred while adding the comment to the work session", {
                variant: "error",
                anchorOrigin: { vertical: "bottom", horizontal: "center" },
                autoHideDuration: 3000,
            });
        },
    });

    const { isLoading: isLoadingDeactivateWorkshopOrder, mutate: deactivateWorkshopOrder } = useDeactivateWorkshopOrderMutation({
        onSuccess: () => {
            enqueueSnackbar("Workshop order finished successfully", {
                variant: "success",
                anchorOrigin: { vertical: "bottom", horizontal: "center" },
                autoHideDuration: 3000,
            });
            refetchWorkshopLine();
        },
        onError: () => {
            enqueueSnackbar("Error occurred while finishing workshop order", {
                variant: "error",
                anchorOrigin: { vertical: "bottom", horizontal: "center" },
                autoHideDuration: 3000,
            });
        },
    });

    const handleStart = () => {
        createWorkline({
            WorkerId: userId,
            WorkshopOrderId: workshopOrderId,
            CheckIn: new Date(),
        });
    };

    const handleStop = () => {
        stopWorkline({
            WorkshopOrderId: workshopOrderId,
            WorkshopLineId: workshopLineId,
            WorkerId: userId,
            CheckOut: new Date(),
        });
    };

    const handleSpareParts = () => {
        history.push(`/workshop/${equipmentId}/${workshopOrderId}/${workshopLineId}/spareparts`);
    };

    const handleDone = () => {
        deactivateWorkshopOrder(workshopOrderId);
    };

    const handleSaveComment = () => {
        updateWorkshopLine({
            WorkshopOrderId: workshopOrderId,
            WorkshopLineId: workshopLineId,
            WorkerId: userId,
            Comment: employeeComment,
        });
    };

    return (
        <>
            <Grid container direction="column">
                <Grid item>
                    <Box component={Paper} variant="outlined" display="flex" flexDirection="column" elevation={0} p={2} mt={1}>
                        <Box display="flex" justifyContent="space-between">
                            <Typography color="textSecondary">{`Work Session`}</Typography>
                            <Box>
                                {isLoadingWorkshopLine ? (
                                    <Chip label={`Loading`} size="small" variant="outlined" disabled />
                                ) : (
                                    <Chip
                                        label={sessionState === "mostRecent" ? "Most Recent" : sessionState === "ongoing" ? "Ongoing" : "Inactive"}
                                        color={sessionState === "mostRecent" ? "default" : sessionState === "ongoing" ? "primary" : "secondary"}
                                        size="small"
                                        variant="outlined"
                                    />
                                )}
                            </Box>
                        </Box>

                        <Box my={1}>
                            <Divider style={{ backgroundColor: "darkgray" }} />
                        </Box>

                        {isLoadingWorkshopLine ? (
                            <LinearProgress />
                        ) : (
                            <>
                                {/* Work session info */}
                                <Box display="flex" justifyContent="space-between">
                                    <Typography color="textSecondary">{`Clocked In`}</Typography>
                                    <Typography>{checkIn ? dayjs(checkIn).format("DD.MM.YY") : "Not started"}</Typography>
                                </Box>

                                <Box display="flex" justifyContent="space-between">
                                    <Typography color="textSecondary">{`Clocked Out`}</Typography>
                                    <Typography>{checkOut ? dayjs(checkOut).format("DD.MM.YY") : "-"}</Typography>
                                </Box>

                                <Box display="flex" justifyContent="space-between">
                                    <Typography color="textSecondary">{`Duration`}</Typography>
                                    <Typography>{duration ? `${duration.toFixed(2)} hours` : "-"}</Typography>
                                </Box>

                                {/* Employee comment */}
                                <Box my={2}>
                                    <Button
                                        variant="contained"
                                        size="small"
                                        fullWidth
                                        endIcon={<EditIcon />}
                                        disabled={sessionState !== "ongoing"}
                                        onClick={() => setIsEmployeeCommentDialogOpen(true)}
                                    >
                                        {`Comment`}
                                    </Button>
                                </Box>

                                <Box mb={1}>
                                    <Divider style={{ backgroundColor: "darkgray" }} />
                                </Box>

                                <Box width="100%" my={1}>
                                    {(sessionState === "mostRecent" || sessionState === "inactive") && (
                                        <LoadingButton onClick={handleStart} disabled={!!(checkIn && !checkOut)} loading={isLoadingCreateWorkline} variant="contained" color="primary" fullWidth>
                                            {`Start`}
                                        </LoadingButton>
                                    )}
                                    {sessionState === "ongoing" && (
                                        <LoadingButton onClick={handleStop} disabled={!(checkIn && !checkOut)} loading={isLoadingStopWorkline} variant="contained" color="secondary" fullWidth>
                                            {`Stop`}
                                        </LoadingButton>
                                    )}
                                </Box>
                            </>
                        )}
                    </Box>
                </Grid>

                <Grid item>
                    <Box display="flex" justifyContent="space-between" flexDirection="column" mt={2}>
                        <Box>
                            <LoadingButton fullWidth variant="contained" color="primary" onClick={handleSpareParts} disabled={sessionState === "inactive" || sessionState === "mostRecent"}>
                                {`Spare Parts`}
                            </LoadingButton>
                        </Box>
                        <Box mt={1}>
                            <LoadingButton
                                fullWidth
                                variant="contained"
                                color="primary"
                                loading={isLoadingDeactivateWorkshopOrder}
                                onClick={handleDone}
                                disabled={sessionState === "inactive" || sessionState === "ongoing"}
                            >
                                {`Done`}
                            </LoadingButton>
                        </Box>
                    </Box>
                </Grid>
            </Grid>
            <Dialog open={isEmployeeCommentDialogOpen}>
                <DialogTitle>{`Add a comment`}</DialogTitle>
                <DialogContent>
                    <Box>
                        <Box my={1}>
                            <Box style={{ overflow: "auto", maxHeight: "100px", whiteSpace: "pre-wrap" }}>
                                <TextField
                                    label="Comment"
                                    variant="filled"
                                    disabled={isLoadingWorkshopLine || sessionState !== "ongoing"}
                                    multiline
                                    fullWidth
                                    minRows={3}
                                    value={employeeComment ?? ""}
                                    onChange={(e) => setEmployeeComment(e.target.value)}
                                    InputProps={{
                                        readOnly: isLoadingUpdateWorkshopLine || sessionState !== "ongoing",
                                    }}
                                />
                            </Box>
                        </Box>

                        <Box display="flex" py={1}>
                            {sessionState === "ongoing" && (
                                <>
                                    <Box>
                                        <LoadingButton
                                            variant="outlined"
                                            color="primary"
                                            size="small"
                                            onClick={() => {
                                                handleSaveComment();
                                                setIsEmployeeCommentDialogOpen(false);
                                            }}
                                            disabled={isLoadingWorkshopLine || isLoadingUpdateWorkshopLine || employeeComment === comment}
                                            loading={isLoadingUpdateWorkshopLine}
                                        >
                                            {`Save`}
                                        </LoadingButton>
                                    </Box>
                                    <Box ml={1}>
                                        <LoadingButton
                                            variant="outlined"
                                            color="secondary"
                                            size="small"
                                            onClick={() => {
                                                setEmployeeComment(comment);
                                                setIsEmployeeCommentDialogOpen(false);
                                            }}
                                        >
                                            {`Cancel`}
                                        </LoadingButton>
                                    </Box>
                                </>
                            )}
                        </Box>
                    </Box>
                </DialogContent>
            </Dialog>
        </>
    );
}

export default WorkshopLineClock;
